import type { CSSProperties, ReactNode } from 'react';

/**
 * Scroll reveal for the marketing pages — content first, motion second.
 *
 * The shared `Reveal` in `@/ui` starts each block at `opacity: 0` and waits
 * for an IntersectionObserver + a fixed-duration animation to finish before
 * the content is readable. At an ordinary scroll speed that trigger lags the
 * scroll position enough that whole sections can sit fully in view at
 * opacity 0 — a screenshot mid-scroll on this page caught exactly that.
 *
 * This version never sets opacity below what CSS scroll-driven animations
 * (`animation-timeline: view()`) compute for the *current* scroll position,
 * so there is no lag to outrun: a screenshot or a paused scroll always shows
 * the correct, already-interpolated frame. Browsers without support simply
 * never start the animation, so the element sits at its plain resting state
 * — fully visible — which is also what print and the pre-hydration paint see.
 * `prefers-reduced-motion` and `@media print` both fall back the same way.
 */
export function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  /*
   * `delay` is still expressed in seconds by every caller, but a scroll-driven
   * animation has no time axis — `animation-delay` is simply ignored on a
   * `view()` timeline, so the stagger those callers asked for never happened.
   * The same intent expressed on the axis this animation actually runs on is a
   * shift in the scroll range: later items begin (and finish) a little further
   * up the page. Capped, so a late item in a long row cannot be pushed past
   * the end of its own range and never play at all.
   */
  const shift = delay ? Math.min(delay * 47, 18) : 0;
  const style = (delay || shift)
    ? ({ '--reveal-shift': `${shift.toFixed(1)}%`, '--reveal-delay': `${delay}s` } as CSSProperties)
    : undefined;
  return (
    <div className={['ledger-reveal', className || ''].filter(Boolean).join(' ')} style={style}>
      {children}
    </div>
  );
}
